import { html } from '@lukekaalim/act-web';
import { Component, Element, h, Node, useEffect, useMemo, useRef, useState } from '@lukekaalim/act';
import { Mini } from '../../defs';
import { LogicDataOf, LogicOf } from '../../defs/logic/all';
import { characterLogic, hitpointsLogic, monsterLogic, movementLogic } from '../../defs/logic/adventure';
import { assetsURLs } from '../../assets';

import classes from './Flag.module.css';
import { Animation, bezier4, Diff, getAnimationPoint, lerp, useAnimation } from '@kosmod/kit';
import { renderBeat } from '@kosmod/graphics-3d';
import { a } from '@kosmod/pancake/kitchen';

export const Flag: Component = ({ children }) => {
  return h(html.div, { className: classes.flag }, children)
}

export type MonsterFlagProps = {
  isGM?: boolean,
  detailed?: boolean,

  mini: Mini,

  hitpoints?: LogicOf<typeof hitpointsLogic>,
  character?: LogicOf<typeof characterLogic>,
  movement?: LogicOf<typeof movementLogic>,
  monster?: LogicOf<typeof monsterLogic>,
}

export const FlagTransition = ({ visible, children }: { visible: boolean, children: Node }) => {
  const ref = useRef<HTMLElement | null>(null);

  const animation = useRef(() => {
    const initial = visible ? 1 : 0;
    return Animation(0, 0, [initial, initial, initial, initial], ([a, b, c, d], p) => bezier4(a, b, c, d, p))
  });

  renderBeat.useBeat((now) => {
    const current = ref.get()!
    const point = getAnimationPoint(animation.get(), now);
    current.style.opacity = point.toString();
    current.style.transform = `translateY(${(1 - point) * 16}px)`
    if (point < 1)
      current.style.pointerEvents = 'none';
    else
      current.style.pointerEvents = 'auto';
  })

  useEffect(() => {
    const next = visible ? 1 : 0;
    const now = performance.now();
    const anim = animation.get();
    const current = getAnimationPoint(anim, now);
    anim.span.start = now;
    anim.span.length = 200;
    anim.values = [current, next, next, next]
  }, [visible])

  return h(html.div, { ref }, children)
};

export type ListTransitionProps = {
  elements: ({
    key: string,
    /**
     * 
     * @param position 
     * @param occupancy 0 -> 1 (entering), 1 -> 2 (exiting)
     * @returns 
     */
    render: (position: Animation<number>, occupancy: Animation<number>) => Node,
    size: number
  } | undefined | false | null)[]
}

const ListTransition: Component<ListTransitionProps> = ({ elements: inputElements }) => {
  type AnimState = {
    key: string,
    occupancy: Animation<number>,
    positionAnim: Animation<number>,
    position: number,
    index: number,
    size: number,
    render: (position: Animation<number>, occupancy: Animation<number>) => Node,
    state: 'entering' | 'exiting'
  };
  const elementStates = useRef<AnimState[]>([]);

  const TransitionElement = useMemo(() => ({ state }: { state: AnimState }) => {
    const ref = useRef<HTMLElement | null>(null);

    renderBeat.useBeat((now) => {
      const current = ref.get()!
      const position = getAnimationPoint(state.positionAnim, now);
      const occupancy = getAnimationPoint(state.occupancy, now);

      current.style.height = state.size + 'px';
      current.style.opacity = occupancy.toString();
      current.style.transform = `translateY(${position}px)`;
    })
    const style = useMemo(() => ({
      position: 'absolute',
      top: '0',
      display: 'flex',
      width: '100%',
      flexDirection: 'column'
    }), [])

    return h(html.div, { ref, style }, state.render(state.positionAnim, state.occupancy))
  }, []);

  const ref = useRef<HTMLElement | null>(null);
  const heightAnim = useRef(Animation(0, 0, [0, 0, 0, 0], ([a, b, c, d], p) => bezier4(a, b, c, d, p)));
  renderBeat.useBeat((now) => {
    const container = ref.get()!
    const height = getAnimationPoint(heightAnim.get(), now);

    container.style.height = height + 'px';
  })

  const output = useMemo(() => {
    const now = performance.now();

    elementStates.set(elementStates.get().filter(state => {
      if (state.state === 'exiting') {
        const exiting = getAnimationPoint(state.occupancy, now);
        return exiting > 0;
      }
      return true;
    }))

    const elements = inputElements.filter(x => !!x);
    const diff = Diff.generate(elementStates.get(), elements, (state, el) => state.state !== 'exiting' && el.key === state.key);

    let distance = 2;
    const margin = 4;

    const nextStates: AnimState[] = [
      ...diff.transform.map((prevIndex, nextIndex): AnimState => {
        const next = elements[nextIndex];
        const position = distance;
        distance += next.size + margin;

        if (prevIndex === -1) {
          return {
            key: next.key, state: 'entering', index: nextIndex,
            occupancy: Animation(now, 200, [0, 1, 1, 1], ([a, b, c, d], p) => bezier4(a, b, c, d, p)),
            positionAnim: Animation(now, 200, [0, position, position, position], ([a, b, c, d], p) => bezier4(a, b, c, d, p)),
            position,
            size: next.size,
            render: next.render,
          }
        }
        const prev = elementStates.get()[prevIndex];

        if (prev.position !== position) {
          return {
            key: next.key,
            state: 'entering',
            index: nextIndex,
            occupancy: prev.occupancy,
            positionAnim: Animation(now, 200, [prev.position, position, position, position], ([a, b, c, d], p) => bezier4(a, b, c, d, p)),
            position,
            size: next.size,
            render: next.render,
          }
        }
        return { ...prev, render: next.render };
      }),
      ...diff.removed.map((prevIndex): AnimState => {
        const prev = elementStates.get()[prevIndex];
        if (prev.state === 'exiting')
          return prev;
        const occupancyStart = getAnimationPoint(prev.occupancy, now);

        return {
          key: prev.key + (Math.random() * 1000).toFixed(4),
          state: 'exiting',
          index: prevIndex,
          occupancy: Animation(now, 200, [occupancyStart, 0, 0, 0], ([a, b, c, d], p) => bezier4(a, b, c, d, p)),
          positionAnim: prev.positionAnim,
          position: prev.position,
          size: prev.size,
          render: prev.render,
        }
      })
    ]
    elementStates.set(nextStates);
    const finalHeight = distance - margin + 2;
    const currentHeight = getAnimationPoint(heightAnim.get(), now);

    heightAnim.set(Animation(now, 200, [currentHeight, finalHeight, finalHeight, finalHeight], heightAnim.get().interpolate));

    return h(html.div, { ref, style: { position: 'relative', display: 'flex', flexDirection: 'column' } }, [
      nextStates.map(state => {
        return h(TransitionElement, { key: state.key, state })
      })
    ]);
  }, [inputElements])



  return output;
}

export const MonsterFlag: Component<MonsterFlagProps> = ({
  isGM,
  detailed,
  mini,
  monster = null,
  hitpoints = null,
  character = null,
  movement = null
}) => {

  const door = mini.logic.find(l => l.key === 'door');

  return h(html.div, { className: classes.flag }, [
    h(html.div, { className: classes.monsterFlagBorder, style: { borderImageSource: `url(${assetsURLs.borders.spikeBlack})` }}, [
      h(ListTransition, { elements: [
        detailed && monster && monster.data.sourceRef && { key: 'monster', render: () => h(html.div, {}, h(SourceRef, {  })), size: 62 },
        character && { key: 'char', render: () => h(html.h3, { className: classes.characterName }, character.data.name), size: 50 },
        hitpoints && { key: 'health', render: () => h(html.div, {}, h(HitpointBar, { hitpoints, showPreview: !isGM })), size: 48 },
      ] }),
      h(html.div, { className: classes.interiorBorder, style: { borderImageSource: `url(${assetsURLs.borders.interiorBevel})` } }),
      //character && h(html.h3, { className: classes.characterName }, character.data.name),
      //hitpoints && h(html.div, {}, h(HitpointBar, { hitpoints, showPreview: !isGM })),
      !!door && h(html.button, {}, door.data.open ? 'Open' : 'Closed')
    ]),
    h(html.img, { src: assetsURLs.cards.simpleSkull, className: classes.monsterSkull }),
  ]);
};

const SourceRef = () => {
  return h(html.div, { className: classes.sourceRefContainer }, [
    h(html.a, { href: 'https://example.com' }, 'source stats'),
    h(html.img, { src: assetsURLs.icons.simpleBook })
  ]);
}

type HPPreviewState =
  | 'healthy'
  | 'hit'
  | 'wounded'
  | 'bloodied'
  | 'spicy'
  | 'dead'

const calcHitpointPreview = (percentage: number): HPPreviewState => {
  if (percentage > 99) {
    return 'healthy';
  }
  if (percentage > 80) {
    return 'hit';
  }
  if (percentage > 50) {
    return 'wounded';
  }
  if (percentage > 9) {
    return 'bloodied';
  }
  if (percentage > 0) {
    return 'spicy';
  }
  return 'dead';
}

const colorsForHitpointPreview = {
  'healthy': { background: 'rgb(152, 206, 143)', border: 'rgb(121, 152, 115)', text: 'rgb(14, 140, 0)' },
  'hit': { background: 'rgb(190, 206, 143)', border: 'rgb(152, 149, 115)', text: 'rgb(140, 133, 0)' },
  'wounded': { background: 'rgb(206, 199, 143)', border: 'rgb(152, 140, 115)', text: 'rgb(140, 107, 0)' },
  'bloodied': { background: '#ce8f8f', border: '#987373', text: '#8c1800' },
  'spicy': { background: '#d9b1e4', border: '#f688e6', text: '#8b028b' },
  'dead': { background: '#3f3f41', border: '#999999', text: '#e2dfe2' },

  'default': { background: '#ce8f8f', border: '#987373', text: '#8c1800' },
}

const HitpointBar: Component<{
  hitpoints: LogicOf<typeof hitpointsLogic>,
  showPreview?: boolean,
}> = ({ hitpoints, showPreview }) => {
  const preview = calcHitpointPreview((hitpoints.data.current / hitpoints.data.maximum) * 100);
  const colors = colorsForHitpointPreview[preview] || colorsForHitpointPreview['default'];

  return h(html.section, { className: classes.flagSectionBackground }, [
    h(html.div, { className: classes.hitpointBar, style: { borderColor: colors.border, backgroundColor: colors.background, color: colors.text } }, [
      h(html.div, { className: classes.hitpointBarContent }),
      showPreview
        ? h(html.div, { className: classes.hitpointBarText }, preview)
        : h(html.div, { className: classes.hitpointBarText }, `${hitpoints.data.current} / ${hitpoints.data.maximum}`)
    ])
  ]);
}


export const HeroFlag = () => {

};
